import React, { useMemo, useState } from 'react'

import { Button, Tooltip, Space, Tag } from 'antd'
import { LinkOutlined, QuestionCircleOutlined, QuestionCircleFilled } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useOperatorData } from '@contexts/operator/OperatorDataContext'
import { useIsStandard } from '@contexts/operator/PlanContext'
import FullDayRangePicker from '@components/shared/FullDayRangePicker'
import ListPageHeader from '@shared/ListPageHeader'
import FulfillmentConceptModal from '@shared/orders/FulfillmentConceptModal'
import TableIndex from '@shared/table/TableIndex'
import { MultipleValue } from '@shared/table/Rules/component'
import FulfillmentStatusLabel from '@shared/fulfillments/FulfillmentStatusLabel'

import { fulfillmentService } from '@services/operator/fulfillmentService'
import { formatDateTime } from '@utils/dateTime'

interface FulfillmentRow {
  id: string;
  number?: string;
  name?: string;
  status?: string;
  merchant_name?: string;
  source_type?: string;
  stock_transfer_order?: { id: string; name: string } | null;
  work_orders?: Array<{ id: string; name: string }>;
  // 物流類型走 fulfillment 層（authoritative）；不再從 orders[] 取
  shipping_type?: { id: string; name?: string; code?: string } | null;
  orders?: Array<{
    id: string;
    name?: string;
    receiver_name?: string;
    sales_channel_name?: string;
  }>;
  total_quantity?: number;
  created_at?: string;
  [key: string]: unknown;
}

export default function FulfillmentList() {
  const { t } = useTranslation()
  const { merchants, merchantShippingTypes, merchantSalesChannels } = useOperatorData()
  const isStandard = useIsStandard()
  const [lastModified, setLastModified] = useState(0)
  const [conceptVisible, setConceptVisible] = useState(false)

  const statusOptions = useMemo(() => [
    'pending', 'allocated', 'partial_allocated', 'out_of_stock',
    'processing', 'shipped', 'canceled', 'recorded'
  ].map(status => ({
    label: t(`shared.statuses.${status}`),
    value: status
  })), [t])

  const merchantOptions = useMemo(() => merchants.map((merchant: { id: string; name: string }) => ({
    label: merchant.name,
    value: merchant.id
  })), [merchants])

  const shippingTypeOptions = useMemo(() => merchantShippingTypes.map((shippingType: { id: string; code: string; name: string }) => ({
    label: t(`shipping_types.${shippingType.code}`, { defaultValue: shippingType.name }),
    value: shippingType.id
  })), [merchantShippingTypes, t])

  const channelOptions = useMemo(() => merchantSalesChannels.map((channel: { id: string; name: string }) => ({
    label: channel.name,
    value: channel.id
  })), [merchantSalesChannels])

  const supportRules = useMemo(() => [
    {
      label: t('shared.orders.list.filters.order_number'),
      component: MultipleValue,
      inputName: 'orders.order_names',
      createable: true,
      defaultPinned: true,
      tooltip: {
        title: t('shared.orders.list.filter_tooltips.order_number.title'),
        content: t('shared.orders.list.filter_tooltips.order_number.content'),
      },
    },
    {
      label: t('shared.picking_lists.generate.filters.task_type'),
      component: MultipleValue,
      inputName: 'fulfillments.source_type',
      defaultOptions: [
        { label: t('enums.fulfillment.source_type.OrderItem'), value: 'OrderItem' },
        { label: t('enums.fulfillment.source_type.WorkOrderItem'), value: 'WorkOrderItem' },
        { label: t('enums.fulfillment.source_type.StockTransferOrder'), value: 'StockTransferOrder' },
      ],
      createable: false,
      tooltip: {
        title: t('operator.fulfillments.list.filter_tooltips.task_type.title'),
        content: t('operator.fulfillments.list.filter_tooltips.task_type.content'),
      },
    },
    {
      label: t('shared.status'),
      component: MultipleValue,
      inputName: 'fulfillments.status',
      defaultOptions: statusOptions,
      createable: false,
      defaultPinned: true,
      tooltip: {
        title: t('operator.fulfillments.list.filter_tooltips.status.title'),
        content: t('operator.fulfillments.list.filter_tooltips.status.content'),
      },
    },
    // 單一 merchant 模式下不顯示 merchant 篩選器
    ...(isStandard ? [] : [{
      label: t('shared.merchant'),
      component: MultipleValue,
      inputName: 'fulfillments.merchant_id',
      defaultOptions: merchantOptions,
      createable: false,
      tooltip: {
        title: t('operator.fulfillments.list.filter_tooltips.merchant.title'),
        content: t('operator.fulfillments.list.filter_tooltips.merchant.content'),
      },
    }]),
    {
      label: t('shared.orders.list.filters.receiver_name'),
      component: MultipleValue,
      inputName: 'orders.receiver_name',
      createable: true,
      tooltip: {
        title: t('shared.orders.list.filter_tooltips.receiver_name.title'),
        content: t('shared.orders.list.filter_tooltips.receiver_name.content'),
      },
    },
    {
      label: t('shared.orders.list.filters.shipping_type'),
      component: MultipleValue,
      // 篩 fulfillment 自身的物流類型（authoritative，與詳細頁 change_shipping_type 同源）；
      // 不再用 orders.shipping_type — 訂單詳細頁的 shipping_type 屬下單宣告語意，不適合做出貨單 filter
      inputName: 'fulfillments.shipping_type_id',
      defaultOptions: shippingTypeOptions,
      createable: false,
      tooltip: {
        title: t('shared.orders.list.filter_tooltips.shipping_type.title'),
        content: t('shared.orders.list.filter_tooltips.shipping_type.content'),
      },
    },
    {
      label: t('shared.orders.list.filters.channel'),
      component: MultipleValue,
      inputName: 'orders.sales_channel_id',
      defaultOptions: channelOptions,
      createable: false,
      tooltip: {
        title: t('shared.orders.list.filter_tooltips.channel.title'),
        content: t('shared.orders.list.filter_tooltips.channel.content'),
      },
    },
    {
      label: t('common.created_at'),
      component: FullDayRangePicker,
      inputName: 'fulfillments.created_at',
      tooltip: {
        title: t('operator.fulfillments.list.filter_tooltips.created_at.title'),
        content: t('operator.fulfillments.list.filter_tooltips.created_at.content'),
      },
    }
  ], [t, statusOptions, merchantOptions, shippingTypeOptions, channelOptions, isStandard])

  const columns = useMemo(() => [
    {
      nameKey: 'models.fulfillment.number',
      sortable: true,
      sortFieldName: 'fulfillments.number',
      width: '200px',
      cell: (record: FulfillmentRow) => (
        <Space direction="vertical" size={0}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Link to={`/fulfillments/${record.id}`} style={{ fontWeight: 'bold', marginRight: 8 }}>
              {record.number}
            </Link>
            <a
              href={`/operator/fulfillments/${record.id}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        </Space>
      )
    },
    {
      nameKey: 'models.fulfillment.name',
      sortable: true,
      sortFieldName: 'fulfillments.name',
      selector: (record: FulfillmentRow) => record.name,
    },
    {
      nameKey: 'models.fulfillment.status',
      width: '120px',
      cell: (row: FulfillmentRow) => <FulfillmentStatusLabel status={row.status} />,
    },
    // 單一 merchant 模式下不顯示 merchant 欄位
    ...(isStandard ? [] : [{
      nameKey: 'shared.merchant',
      sortable: false,
      selector: (record: FulfillmentRow) => record.merchant_name,
      wrap: true,
    }]),
    {
      nameKey: 'operator.fulfillments.list.columns.source_documents',
      sortable: false,
      cell: (record: FulfillmentRow) => {
        const sourceType = record.source_type
        if (sourceType === 'stock_transfer_order' && record.stock_transfer_order) {
          return (
            <Space direction="vertical" size={2}>
              <Tag color="purple" style={{ margin: 0 }}>{t('enums.fulfillment.source_type.StockTransferOrder')}</Tag>
              <Link to={`/stock_transfer_orders/${record.stock_transfer_order.id}`} style={{ fontSize: '13px' }}>
                {record.stock_transfer_order.name}
              </Link>
            </Space>
          )
        }
        if (sourceType === 'work_order' && record.work_orders && record.work_orders.length > 0) {
          return (
            <Space direction="vertical" size={2}>
              <Tag color="orange" style={{ margin: 0 }}>{t('enums.fulfillment.source_type.WorkOrderItem')}</Tag>
              {record.work_orders.map((wo) => (
                <Link key={wo.id} to={`/work_orders/${wo.id}`} style={{ fontSize: '13px' }}>
                  {wo.name}
                </Link>
              ))}
            </Space>
          )
        }
        // 預設：一般訂單
        return (
          <Space direction="vertical" size={0}>
            {record.orders?.map((order) => (
              <Link key={order.id} to={`/orders/${order.id}`} style={{ fontSize: '13px' }}>
                {order.name}
              </Link>
            ))}
          </Space>
        )
      }
    },
    {
      nameKey: 'operator.fulfillments.list.columns.receiver',
      sortable: false,
      cell: (record: FulfillmentRow) => (
        <Space direction="vertical" size={0}>
          {record.orders?.map((order, idx) => (
            <span key={`${order.id}-${idx}`} style={{ fontSize: '13px', display: 'block' }}>
              {order.receiver_name}
            </span>
          ))}
        </Space>
      )
    },
    {
      nameKey: 'operator.fulfillments.list.columns.shipping_info',
      sortable: false,
      // fulfillment 一張只會掛一個 shipping_type（authoritative）；不再 per-order 重覆顯示
      cell: (record: FulfillmentRow) => {
        const name = record.shipping_type?.name
        const code = record.shipping_type?.code
        const label = code ? t(`shipping_types.${code}`, { defaultValue: name ?? '-' }) : (name ?? '-')
        return <span style={{ fontSize: '12px' }}>{label}</span>
      }
    },
    {
      nameKey: 'models.order.sales_channel',
      sortable: false,
      cell: (record: FulfillmentRow) => (
        <Space direction="vertical" size={0}>
          {record.orders?.map((order, idx) => (
            <span key={`${order.id}-${idx}`} style={{ fontSize: '12px' }}>
              {order.sales_channel_name}
            </span>
          ))}
        </Space>
      )
    },
    {
      name: (
        <Space size={4}>
          {t('operator.fulfillments.list.columns.total_quantity')}
          <Tooltip title={t('operator.fulfillments.list.column_tooltips.items')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: false,
      width: '100px',
      selector: (record: FulfillmentRow) => record.total_quantity,
      center: true,
    },
    {
      nameKey: 'common.created_at',
      sortable: true,
      sortFieldName: 'fulfillments.created_at',
      width: '180px',
      selector: (record: FulfillmentRow) => formatDateTime(record.created_at as string),
    },
  ], [t, isStandard])

  return (
    <div>
      <ListPageHeader
        items={['orders', 'orders.fulfillments']}
        guideButton={
          <Tooltip title={t('concepts.fulfillments.tooltip')}>
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleFilled style={{ color: '#1890ff' }} />}
              onClick={() => setConceptVisible(true)}
              style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
            />
          </Tooltip>
        }
      />
      <TableIndex
        tableId="OperatorFulfillmentsList"
      columns={columns}
      supportRules={supportRules}
      service={fulfillmentService}
      defaultQuery={{
        sorting_field: 'fulfillments.id',
        sorting_direction: 'desc',
      }}
      lastModified={lastModified}
      setLastModified={setLastModified}
      searchable={{
        match: 'prefix',
        tooltipContent: t('operator.fulfillments.list.search_keywords_tooltip_content'),
      }}
      />
      <FulfillmentConceptModal
        visible={conceptVisible}
        onClose={() => setConceptVisible(false)}
      />
    </div>
  )
}
